import { useCallback, useState } from 'react';
import type { LoaderFunctionArgs } from 'react-router';
import { redirect, useLoaderData } from 'react-router';
import { postAccountIntegrateExecuteWithToken } from '../../api/apiClient';
import { Unauthorized } from '../../components/Error/Unauthorized';
import { IntegrationAlreadyRegistered } from '../../features/integration/components/IntegrationAlreadyRegistered';
import { IntegrationComplete } from '../../features/integration/components/IntegrationComplete';
import { IntegrationExecuteConsent } from '../../features/integration/components/IntegrationExecuteConsent';
import { IntegrationGuarantorEmailConflict } from '../../features/integration/components/IntegrationGuarantorEmailConflict';
import type { IntegrationConflictCodeValue } from '../../features/integration/error-codes';
import { IntegrationConflictCode, extractConflictCode } from '../../features/integration/error-codes';
import { hasIntegratedSubEmail } from '../../features/integration/integration-status';
import { isHighschoolEmail } from '../../features/highschool';
import { requireSignedIn } from '../../utils/auth/require-signed-in';

export async function integrationVerifyLoader({
  request,
}: LoaderFunctionArgs): Promise<{ status: number; token?: string; alreadyIntegrated: boolean }> {
  const url = new URL(request.url);

  // セッションの無いブラウザでメールリンクを開いた場合は、 token を付与した状態でこのページに戻れるように指定しでログイン画面にリダイレクト
  const returnTo = `${url.pathname}${url.search}` as `/${string}`;
  const user = await requireSignedIn(returnTo);

  // N高生以外が primary account の場合以外はこのページはアクセス不可
  if (!isHighschoolEmail(user.email)) {
    throw redirect('/unauthorized');
  }

  try {
    // token は TTL 内なら再検証を通るため、統合完了後にメールのリンクを開くと必ず失敗する統合を試みることになる。
    // 実行前に統合済みかを確認し、同意画面ではなく登録済みの案内を出す
    const alreadyIntegrated = await hasIntegratedSubEmail();

    // 統合の実行はユーザーの同意後に行うため、loader では token の受け渡しのみを行う
    const token = url.searchParams.get('token');
    if (!token) {
      return { status: 401, alreadyIntegrated };
    }

    return { status: 200, token, alreadyIntegrated };
  } finally {
    // search param を削除
    history.replaceState(null, '', location.pathname);
  }
}

export function IntegrationVerifyPage() {
  const { status, token, alreadyIntegrated } = useLoaderData<typeof integrationVerifyLoader>();
  const [executeStatus, setExecuteStatus] = useState<number | undefined>(undefined);
  const [returnUrl, setReturnUrl] = useState<string | undefined>(undefined);
  const [conflictCode, setConflictCode] = useState<IntegrationConflictCodeValue | undefined>(undefined);

  const execute = useCallback(async () => {
    if (!token) {
      return;
    }
    try {
      const { data, error, response } = await postAccountIntegrateExecuteWithToken({ token });
      // 完了画面を必ず挟み、ログイン元アプリへ戻るかどうかはユーザーの操作に委ねる。
      // returnUrl はログイン元アプリを特定できない場合 (best-effort) に省略され、その場合は戻り先をマイページ内とする
      setReturnUrl(data?.returnUrl);
      setConflictCode(response.status === 409 ? extractConflictCode(error) : undefined);
      setExecuteStatus(response.status);
    } catch {
      // 統合実行中のエラーは認証エラーとして処理する
      setExecuteStatus(401);
    }
  }, [token]);

  // token の有無より先に判定する。統合済みであれば token 切れであっても登録済みの案内の方が状況を正しく説明できる
  if (alreadyIntegrated) {
    return <IntegrationAlreadyRegistered />;
  }

  if (status !== 200 || !token) {
    return <Unauthorized />;
  }

  switch (executeStatus) {
    // 未実行の場合は同意画面を表示し、同意を得てから統合を実行する
    case undefined: {
      return <IntegrationExecuteConsent execute={execute} />;
    }
    case 201: {
      return <IntegrationComplete returnUrl={returnUrl} />;
    }
    // 競合の原因ごとにユーザに知らせる
    case 409: {
      if (conflictCode === IntegrationConflictCode.AccountAlreadyIntegrated) {
        return <IntegrationAlreadyRegistered />;
      }
      return <IntegrationGuarantorEmailConflict />;
    }
    default: {
      return <Unauthorized />;
    }
  }
}
